import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Radar, Store, ArrowRight, Ticket, ChevronDown, ChevronUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import BackButton from "@/components/hola/BackButton";
import PasswordInput from "@/components/hola/PasswordInput";
import api from "@/lib/api";
import { toast } from "sonner";

export default function MerchantRegister() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    business_name: "",
    phone: "",
    password: "",
    category: "cafeteria",
    advisor_name: "",
  });
  const [loading, setLoading] = useState(false);
  const [showPin, setShowPin] = useState(false);
  const [pinCode, setPinCode] = useState("");

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/register", form);
      localStorage.setItem("holaradar_merchant_token", data.token);
      toast.success("¡Negocio registrado! Elige tu plan y reporta tu pago.");
      navigate("/dashboard/billing");
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo registrar");
    } finally {
      setLoading(false);
    }
  };

  const redeemPinAfterRegister = async () => {
    const pin = pinCode.trim();
    if (!pin) {
      toast.error("Escribe un PIN antes de canjear");
      return;
    }
    if (!form.business_name || !form.phone || !form.password) {
      toast.error("Primero llena los datos del negocio (nombre, celular, contraseña)");
      return;
    }
    setLoading(true);
    try {
      // 1) Pre-validar el PIN ANTES de tocar la tabla merchants (evita comercios huérfanos)
      const check = await api.get(`/public/pins/check/${encodeURIComponent(pin)}`);
      if (!check.data?.valid) {
        const reason = check.data?.reason;
        const msg = reason === "already_used"
          ? "Este PIN ya fue canjeado por otro comercio"
          : "PIN no válido. Verifica el código.";
        toast.error(msg);
        setLoading(false);
        return;
      }

      // 2) Registrar comercio. Si ya existe con ese celular + nombre, hacer login (retry safe)
      let token = null;
      try {
        const { data } = await api.post("/auth/register", form);
        token = data.token;
      } catch (regErr) {
        const detail = regErr.response?.data?.detail || "";
        const isDup = typeof detail === "string" && detail.includes("Ya tienes un comercio");
        if (!isDup) throw regErr;
        // El comercio ya existía (retry). Intentamos login con las mismas credenciales.
        const { data: login } = await api.post("/auth/login", {
          phone: form.phone,
          password: form.password,
        });
        if (login.requires_selection) {
          // Buscar por nombre exacto y hacer login/select
          const match = (login.merchants || []).find(
            (m) => (m.business_name || "").trim().toLowerCase() === form.business_name.trim().toLowerCase()
          );
          if (!match) {
            toast.error("Ya tienes varios comercios con ese celular. Ve a Iniciar sesión.");
            setLoading(false);
            return;
          }
          const { data: sel } = await api.post("/auth/login/select", {
            phone: form.phone,
            password: form.password,
            merchant_id: match.merchant_id,
          });
          token = sel.token;
        } else {
          token = login.token;
        }
      }
      localStorage.setItem("holaradar_merchant_token", token);

      // 3) Canjear el PIN
      const r = await api.post("/auth/redeem-pin", { pin_code: pin });
      const days = r.data?.grant_days || 30;
      toast.success(`¡PIN canjeado! Tu radar está activo por ${days} días.`);
      navigate("/dashboard/profile");
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === "object" ? detail?.message : detail;
      toast.error(msg || "No se pudo canjear el PIN");
    } finally {
      setLoading(false);
    }
  };

  const handleGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-lg mx-auto px-6 py-10">
        <div className="mb-4 -ml-2">
          <BackButton to="/" label="Regresar" testid="register-back-btn" />
        </div>
        <Link to="/" className="flex items-center gap-2 mb-10">
          <div className="w-10 h-10 rounded-xl bg-[#0055FF] text-white grid place-items-center">
            <Radar className="w-5 h-5" />
          </div>
          <span className="font-heading font-extrabold text-lg">HOLA RADAR</span>
        </Link>

        <h1 className="font-heading text-4xl font-extrabold tracking-tight">Registra tu negocio</h1>
        <p className="text-neutral-500 mt-2"><strong className="text-black">NEGOCIO PEQUEÑO</strong> $490 MXN mensuales o <strong className="text-black">NEGOCIO GRANDE</strong> $990 MXN mensuales. Sin contrato forzoso.</p>

        <Button
          onClick={handleGoogle}
          variant="outline"
          className="mt-8 w-full h-12 rounded-full border-neutral-300 font-semibold"
          data-testid="google-register-btn"
        >
          Continuar con Google
        </Button>

        <div className="my-6 flex items-center gap-3 text-xs text-neutral-400">
          <div className="h-px bg-neutral-200 flex-1"></div>
          <span>o registrarte con celular</span>
          <div className="h-px bg-neutral-200 flex-1"></div>
        </div>

        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label>Nombre del negocio</Label>
            <Input
              value={form.business_name}
              onChange={(e) => upd("business_name", e.target.value)}
              placeholder="Cafetería Origen"
              className="mt-1.5 h-11 rounded-xl"
              required
              data-testid="reg-name-input"
            />
          </div>
          <div>
            <Label>Categoría</Label>
            <select
              value={form.category}
              onChange={(e) => upd("category", e.target.value)}
              className="mt-1.5 w-full h-11 rounded-xl border border-neutral-200 px-3 text-sm bg-white"
              data-testid="reg-category-select"
            >
              <option value="cafeteria">Cafetería</option>
              <option value="restaurante">Restaurante</option>
              <option value="ropa">Ropa</option>
              <option value="belleza">Belleza</option>
              <option value="tecnologia">Tecnología</option>
              <option value="general">Otro</option>
            </select>
          </div>
          <div>
            <Label>Celular <span className="text-neutral-400 font-normal">(10 dígitos)</span></Label>
            <Input
              value={form.phone}
              onChange={(e) => upd("phone", e.target.value.replace(/\D/g, "").slice(0, 10))}
              placeholder="5512345678"
              inputMode="numeric"
              autoComplete="tel-national"
              maxLength={10}
              pattern="[0-9]{10}"
              className="mt-1.5 h-11 rounded-xl"
              required
              data-testid="reg-phone-input"
            />
          </div>
          <div>
            <Label>Contraseña</Label>
            <PasswordInput
              value={form.password}
              onChange={(e) => upd("password", e.target.value)}
              placeholder="Mínimo 6 caracteres"
              minLength={6}
              autoComplete="new-password"
              className="mt-1.5 h-11 rounded-xl"
              required
              testid="reg-password-input"
            />
          </div>
          <div>
            <Label className="flex items-center gap-2">
              Escribe el nombre de tu asesor comercial
              <span className="text-[10px] uppercase tracking-widest text-neutral-400 font-bold">Opcional</span>
            </Label>
            <Input
              value={form.advisor_name}
              onChange={(e) => upd("advisor_name", e.target.value)}
              placeholder="Ej: Juan Pérez (déjalo vacío si no te lo refirió nadie)"
              className="mt-1.5 h-11 rounded-xl"
              maxLength={120}
              data-testid="reg-advisor-name-input"
            />
          </div>
          <Button
            type="submit"
            disabled={loading}
            className="w-full h-12 rounded-full bg-[#0055FF] hover:bg-[#0044cc] font-bold"
            data-testid="reg-submit-btn"
          >
            <Store className="w-4 h-4" /> {loading ? "Creando..." : "Crear negocio"}
            <ArrowRight className="w-4 h-4" />
          </Button>
          <p className="text-xs text-neutral-500 text-center leading-relaxed -mt-1">
            Después de crear tu negocio te llevamos a la pasarela de pago donde puedes pagar con tarjeta, OXXO o transferencia SPEI.
          </p>

          {/* PIN de acceso gratuito — flujo alternativo (sin pago) */}
          <div className="pt-4 border-t border-neutral-200">
            <button
              type="button"
              onClick={() => setShowPin((v) => !v)}
              className="w-full flex items-center justify-between rounded-2xl border border-dashed border-neutral-300 bg-neutral-50 hover:bg-neutral-100 px-4 py-3 text-sm font-semibold text-neutral-700 transition-colors"
              data-testid="reg-pin-toggle"
            >
              <span className="flex items-center gap-2">
                <Ticket className="w-4 h-4 text-[#FF007A]" />
                ¿Tienes un PIN de acceso gratuito?
              </span>
              {showPin ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>
            {showPin && (
              <div className="mt-3 rounded-2xl bg-[#FFF0F7] border border-[#FF007A]/20 p-4 space-y-3" data-testid="reg-pin-panel">
                <div>
                  <Label className="text-xs uppercase tracking-widest text-[#FF007A] font-bold">Código PIN</Label>
                  <Input
                    value={pinCode}
                    onChange={(e) => setPinCode(e.target.value.toUpperCase())}
                    placeholder="XXXX-XXXX-XXXX"
                    maxLength={14}
                    className="mt-1.5 h-11 rounded-xl font-mono tracking-widest text-center bg-white"
                    data-testid="reg-pin-input"
                  />
                </div>
                <p className="text-xs text-neutral-600 leading-relaxed">
                  Con un PIN válido obtienes <strong>30 días de acceso activo</strong> sin pagar. Este botón crea tu negocio y canjea el PIN de una vez.
                </p>
                <Button
                  type="button"
                  onClick={redeemPinAfterRegister}
                  disabled={loading || !pinCode.trim()}
                  className="w-full h-11 rounded-full bg-[#FF007A] hover:bg-[#e5006e] font-bold"
                  data-testid="reg-pin-redeem-btn"
                >
                  <Ticket className="w-4 h-4" /> {loading ? "Canjeando..." : "Canjear PIN"}
                </Button>
              </div>
            )}
          </div>
        </form>

        <p className="text-sm text-neutral-500 mt-6 text-center">
          ¿Ya tienes cuenta?{" "}
          <Link to="/login" className="text-[#0055FF] font-bold">Entrar</Link>
        </p>
      </div>
    </div>
  );
}
